// src/pages/skills/Skills.styled.tsx
import styled, { keyframes } from "styled-components";

export const SkillsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
`;

export const PageTitle = styled.h1`
  font-size: 2.75rem;
  font-weight: 800;
  margin-bottom: 2.5rem;
  color: #00bcd4;
  text-align: center;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 5px;
    background: linear-gradient(90deg, #2196f3, #00bcd4);
    border-radius: 3px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  margin: 2.5rem 0 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  position: relative;
  padding-left: 1rem;
  border-left: 4px solid #6c63ff;
`;

export const CategoryFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 2rem 0;
  justify-content: center;
`;

export const CategoryButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.25rem;
  border-radius: 30px;
  border: none;
  background-color: ${props => (props.active ? "#6C63FF" : "#f0f0f0")};
  color: ${props => (props.active ? "white" : "#333")};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${props => (props.active ? "#5046e4" : "#e0e0e0")};
    transform: translateY(-2px);
  }

  i {
    font-size: 1.2rem;
  }
`;

export const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2.5rem;
`;

export const SkillCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 1.75rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-top: 4px solid #2196f3;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    border-top-color: #00bcd4;
  }
`;

export const SkillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
`;

export const SkillIconWrapper = styled.div`
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  border-radius: 14px;
  box-shadow: 0 4px 8px rgba(33, 150, 243, 0.15);
  
  i {
    font-size: 28px;
    color: #1976d2;
  }

  img {
    width: 28px;
    height: 28px;
    background-color: #ffffff;
    border-radius: 6px;
    padding: 2px;
    box-shadow: 0 0 0 1px rgba(0,0,0,0.06);
  }
`;

export const SkillInfo = styled.div`
  flex: 1;
`;

export const SkillName = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.25rem 0;
`;

export const SkillCategory = styled.span`
  font-size: 0.9rem;
  color: #2196f3;
  font-weight: 500;
`;

export const SkillBarContainer = styled.div`
  margin: 1rem 0;
`;

export const SkillBarInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;

  span {
    font-size: 1rem;
    color: #333;
    font-weight: 600;
  }
`;

export const SkillBar = styled.div`
  height: 12px;
  background-color: #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const fillAnimation = keyframes`
  from {
    width: 0%;
  }
`;

export const SkillLevel = styled.div<{ width: number }>`
  height: 100%;
  width: ${props => props.width}%;
  background: linear-gradient(90deg, #2196f3, #00bcd4);
  border-radius: 6px;
  animation: ${fillAnimation} 1.5s ease-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

export const SkillDescription = styled.p`
  font-size: 1rem;
  color: #444;
  margin-top: 1.25rem;
  line-height: 1.7;
  background-color: #f5f9ff;
  padding: 1rem;
  border-radius: 8px;
  border-left: 3px solid #2196f3;
`;

export const SkillMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 2rem;
  font-size: 0.95rem;
  color: #555;
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;

  div {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      color: #2196f3;
      font-size: 1.1rem;
    }
  }
`;

export const ProjectsSection = styled.section`
  margin-top: 3rem;
`;

export const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

export const ProjectCard = styled.div`
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

export const ProjectImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
`;

export const ProjectContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

export const ProjectTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
`;

export const ProjectDescription = styled.p`
  font-size: 0.95rem;
  color: #555;
  margin-bottom: 1rem;
  line-height: 1.6;
`;

export const SkillTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

export const SkillTag = styled.span`
  padding: 0.25rem 0.75rem;
  background-color: #f0f0ff;
  color: #6c63ff;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
`;

export const ButtonsWrapper = styled.div`
  margin-top: auto;
  padding: 0 1.5rem 1.5rem;
  display: flex;
  gap: 1rem;
`;
